import { createContext, ReactNode, useCallback, useContext, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { LOGOUT, Logout } from "@store/user/userActions";
import { RootEntity, RootState } from "@types-runway/state";
import { User } from "@types-runway/user";

// The signed-in admin as the admin shell shows them (sidebar footer, sign-out).
// A narrowed view of the account record: screens never read `state.user`.
export interface AdminIdentity {
  id: string;
  fullName: string;
  email: string;
}

export interface AdminSession {
  admin: AdminIdentity;
  signOut: () => void;
}

const AdminSessionContext = createContext<AdminSession | null>(null);

type AdminAccount = Pick<User, "id" | "first_name" | "last_name" | "email">;

const toAdminIdentity = (user: AdminAccount): AdminIdentity => {
  const fullName = `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim();
  return { id: user.id, fullName: fullName || user.email, email: user.email };
};

// Session state of the rebuilt admin: who is signed in and how they leave.
// Everything that comes from the API lives in Redux, not here (see
// docs/architecture/admin-module.md, "Where state lives").
export const AdminSessionProvider = ({ children }: { children: ReactNode }) => {
  const dispatch = useDispatch();
  const user = useSelector<RootState, RootEntity<User>>((state) => state.user);
  const { id, first_name: firstName, last_name: lastName, email } = user.data;

  const admin = useMemo(
    () => toAdminIdentity({ id, first_name: firstName, last_name: lastName, email }),
    [id, firstName, lastName, email]
  );
  const signOut = useCallback(() => {
    const logout: Logout = { type: LOGOUT, userId: id };
    dispatch(logout);
  }, [dispatch, id]);
  const value = useMemo(() => ({ admin, signOut }), [admin, signOut]);

  return <AdminSessionContext.Provider value={value}>{children}</AdminSessionContext.Provider>;
};

export const useAdminSession = (): AdminSession => {
  const session = useContext(AdminSessionContext);
  if (!session) {
    throw new Error("useAdminSession must be used inside AdminSessionProvider");
  }
  return session;
};
